import { type } from 'arktype'

import { TechnicalError } from '@orus.eu/error'

import { logger } from '../../lib/logger.ts'

/** Reaching this many identical bounces in a row with no authorized navigation in between is reported as a loop */
const LOOP_BOUNCE_THRESHOLD = 3

/**
 * A loop bounces on its own, so consecutive bounces are seconds apart. A longer gap means the user came back to a
 * protected page while still logged out (back button, link opened again), which starts a new episode
 */
const LOOP_MAX_BOUNCE_GAP_MS = 10_000

const STORAGE_KEY = 'orus.login-bounce'

/**
 * Explains the bounces that happened before Sentry loaded or on an earlier page load. Two bounces with the same
 * `documentTimeOriginMs` (`performance.timeOrigin`) happened in the same document. The path has no query or hash,
 * which can hold tokens.
 */
const bounceSchema = type({ atMs: 'number', documentTimeOriginMs: 'number', browserPath: 'string' })

const bounceStateSchema = type({
  loginHref: 'string',
  count: 'number',
  lastBounceAtMs: 'number',
  // Absent from an episode that a release without this history started
  'bounces?': bounceSchema.array(),
})

/**
 * Records a bounce to the login page and reports a redirect loop to Sentry once the same login href has
 * been produced {@link LOOP_BOUNCE_THRESHOLD} times in a row, each within {@link LOOP_MAX_BOUNCE_GAP_MS} of the
 * previous one, without an authorized navigation in between
 *
 * {@link buildLoginHref} is idempotent, so the redirect URL no longer grows on each hop
 * {@link resetLoginBounce} clears the count as soon as the user lands authorized or logs in interactively,
 * so legitimate re-logins never accumulate toward the threshold
 */
export function recordLoginBounce(loginHref: string): void {
  const previous = readBounceState()
  const bounceAtMs = currentEpochMs()
  const continuesEpisode =
    previous?.loginHref === loginHref && bounceAtMs - previous.lastBounceAtMs <= LOOP_MAX_BOUNCE_GAP_MS
  const count = continuesEpisode ? previous.count + 1 : 1
  const bounce = {
    atMs: bounceAtMs,
    documentTimeOriginMs: performance.timeOrigin,
    browserPath: window.location.pathname,
  }
  // Up to the threshold, so the first report carries every bounce of the episode
  const bounces = [...(continuesEpisode ? (previous.bounces ?? []) : []), bounce].slice(-LOOP_BOUNCE_THRESHOLD)

  writeBounceState({ loginHref, count, lastBounceAtMs: bounceAtMs, bounces })

  if (count >= LOOP_BOUNCE_THRESHOLD) {
    logger.error(
      new TechnicalError('Login redirect loop detected', {
        context: { loginHref, count, currentHref: window.location.href, bounces },
      }),
    )
  }
}

/** Clears the bounce counter. Call when the user reaches an authorized page or logs in interactively. */
export function resetLoginBounce(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage unavailable (private mode / disabled): nothing to clear
  }
}

/**
 * Elapsed-time measurement only, never shown or sent as a business date: the bounces of a loop span full page
 * reloads, so a page-relative `performance.now()` alone cannot compare them
 */
function currentEpochMs(): number {
  return performance.timeOrigin + performance.now()
}

function readBounceState(): typeof bounceStateSchema.infer | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = bounceStateSchema(JSON.parse(raw))
    return parsed instanceof type.errors ? null : parsed
  } catch {
    return null
  }
}

function writeBounceState(state: typeof bounceStateSchema.infer): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Storage unavailable (private mode / disabled): detection degrades to best-effort, never blocks navigation
  }
}
